import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, WordsReveal } from "@/components/site/Reveal";
import LazyImage from "@/components/site/LazyImage";
import { SITE } from "@/data/site";
import { ABOUT_RESORT } from "@/data/content";

export default function AboutResort() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const y2 = useTransform(scrollYProgress, [0, 1], [-30, 50]);

  return (
    <section ref={ref} data-testid="about-resort" className="relative overflow-hidden bg-ivory py-14 md:py-20">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid items-center gap-12 md:grid-cols-12 md:gap-16">
          {/* Images */}
          <div className="order-2 md:order-1 md:col-span-6">
            <div className="grid grid-cols-2 gap-4 sm:gap-5">
              <motion.div style={{ y: y1 }} className="mt-8 sm:mt-12">
                <LazyImage src={SITE.aboutWelcome1} alt="Vruksha Valley cottages" fit="cover" className="aspect-[3/4] rounded-2xl" />
              </motion.div>
              <motion.div style={{ y: y2 }}>
                <LazyImage src={SITE.aboutWelcome2} alt="Vruksha Valley resort" fit="cover" className="aspect-[3/4] rounded-2xl" />
              </motion.div>
            </div>
          </div>

          {/* Copy */}
          <div className="order-1 md:order-2 md:col-span-6">
            <Reveal><p className="mb-5 text-xs uppercase tracking-widest-2 text-brown md:text-sm">{ABOUT_RESORT.overline}</p></Reveal>
            <h2 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-forest sm:text-5xl md:text-6xl">
              <WordsReveal text={ABOUT_RESORT.heading} />
            </h2>
            {ABOUT_RESORT.paragraphs.map((p, i) => (
              <Reveal key={i} delay={0.15 + i * 0.1}>
                <p className="mt-6 text-charcoal/70">{p}</p>
              </Reveal>
            ))}

            <Reveal delay={0.3}>
              <ul className="mt-8 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                {ABOUT_RESORT.highlights.map((h) => (
                  <li key={h} className="flex items-center gap-3 text-charcoal/80">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-moss/15"><Check className="h-3 w-3 text-moss" /></span>
                    {h}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.4}>
              <Link
                to="/about"
                data-testid="about-resort-cta"
                className="group mt-9 inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-moss"
              >
                Discover Our Story
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
